import type { TimelineEntryIcon, TimelineEntryTone } from '@zealot/ui/components/activity/timeline-entry';
import { Stack } from '@zealot/ui/components/stack';
import { cn } from '@zealot/ui/lib/utils';
import { cva } from 'class-variance-authority';
import { Calendar, MessageSquare, RefreshCw, Settings, User } from 'lucide-react';

const ICONS = {
  note: MessageSquare,
  date: Calendar,
  status: RefreshCw,
  settings: Settings,
  person: User,
} as const;

// gloss
const dotVariants = cva('shrink-0 rounded-full border-2 bg-background', {
  variants: {
    tone: {
      default: 'border-primary',
      success: 'border-success',
      destructive: 'border-destructive',
    },
    align: {
      row: 'mt-1',
      card: 'mt-4',
    },
    shape: {
      dot: 'h-3 w-3',
      icon: 'grid h-6 w-6 place-items-center [&>svg]:size-3 [&>svg]:text-muted-foreground',
    },
  },
  compoundVariants: [
    { shape: 'icon', align: 'row', className: '-mt-0.5' },
    { shape: 'icon', align: 'card', className: 'mt-2.5' },
  ],
  defaultVariants: {
    tone: 'default',
    align: 'row',
    shape: 'dot',
  },
});

// gloss
type TimelineRailProps = {
  isLast: boolean;
  tone?: TimelineEntryTone;
  muted?: boolean;
  align?: 'row' | 'card';
  icon?: TimelineEntryIcon;
};

// gloss
export const TimelineRail = ({ isLast, tone, muted, align, icon }: TimelineRailProps) => {
  const Icon = icon ? ICONS[icon] : undefined;

  return (
    <Stack gap={0} align="center" className="w-6 self-stretch" data-slot="activity-timeline-rail">
      <span
        className={cn(
          dotVariants({ tone: tone ?? 'default', align: align ?? 'row', shape: Icon ? 'icon' : 'dot' }),
          muted && 'border-border',
        )}
      >
        {Icon && <Icon aria-hidden="true" />}
      </span>
      {!isLast && <span className="mt-1 w-px flex-1 bg-border" />}
    </Stack>
  );
};
